import { Button } from "@package/subframe/components/Button";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherAlertTriangle } from "@subframe/core";
import { useMemo, useState } from "react";

import type { ColdStorageResponseDto } from "@/generated/model";
import { signedDecimalInputProps } from "@/lib/is-ios";
import { sanitizeDecimalInput } from "@/lib/number-input";

/**
 * Practitioner-facing entry form for a single fridge / freezer temperature
 * reading. Unit is selected at the page level (LogSessionPage), so this form
 * only handles temperature + conditional notes.
 *
 * Behaviour driven by NAU-95 ACs:
 *   - AC4: Thresholds come from the unit prop, so out-of-range eval works
 *     offline.
 *   - AC5/AC6: When the entered temperature falls outside the unit's target
 *     range, a banner appears and the notes field becomes required. Submit
 *     is blocked until a non-empty note exists.
 */
interface FridgeTempLogFormProps {
  unit: ColdStorageResponseDto;
  onSubmit: (args: { temperature: number; notes?: string }) => Promise<void>;
  isSubmitting: boolean;
}

export default function FridgeTempLogForm({
  unit,
  onSubmit,
  isSubmitting,
}: FridgeTempLogFormProps) {
  const [temperatureRaw, setTemperatureRaw] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const parsedTemperature = useMemo(() => {
    if (temperatureRaw.trim() === "") return null;
    const n = Number(temperatureRaw);
    return Number.isFinite(n) ? n : null;
  }, [temperatureRaw]);

  // Local out-of-range eval mirrors ThresholdEvaluator on the server. Boundary
  // values are in range (>= min && <= max). The server still snapshots
  // thresholds onto the entry at write time — this is just to drive the UI.
  const isOutOfRange =
    parsedTemperature !== null &&
    (parsedTemperature < unit.targetTempMin ||
      parsedTemperature > unit.targetTempMax);

  const noteRequired = isOutOfRange;
  const noteMissing = noteRequired && notes.trim().length === 0;
  const submitDisabled =
    isSubmitting || parsedTemperature === null || noteMissing;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (parsedTemperature === null) {
      setError("Please enter a numeric temperature.");
      return;
    }
    if (noteMissing) {
      setError("A note is required when the reading is out of range.");
      return;
    }

    try {
      await onSubmit({
        temperature: parsedTemperature,
        notes: notes.trim() || undefined,
      });
      setTemperatureRaw("");
      setNotes("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save entry";
      setError(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
      <TextField
        label={`Temperature (°C) — ${unit.name}`}
        helpText={`Target range: ${unit.targetTempMin}°C to ${unit.targetTempMax}°C`}
      >
        <TextField.Input
          type="text"
          {...signedDecimalInputProps()}
          pattern="-?[0-9]*\.?[0-9]*"
          placeholder="e.g. 4.2"
          value={temperatureRaw}
          onChange={(e) =>
            setTemperatureRaw(sanitizeDecimalInput(e.target.value))
          }
          disabled={isSubmitting}
          autoFocus
        />
      </TextField>

      {isOutOfRange && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-warning-200 bg-warning-50 p-3 text-warning-900"
        >
          <FeatherAlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
          <div className="flex flex-col gap-1">
            <span className="text-body-bold font-body-bold">
              Out of range — note required
            </span>
            <span className="text-caption font-caption">
              {parsedTemperature}°C is outside {unit.name}&apos;s target range
              of {unit.targetTempMin}°C to {unit.targetTempMax}°C. Add a note
              explaining what happened before submitting.
            </span>
          </div>
        </div>
      )}

      <TextArea
        label={noteRequired ? "Notes (required)" : "Notes (optional)"}
        helpText={
          noteRequired ? "Required because the reading is out of range" : ""
        }
        error={noteMissing}
      >
        <TextArea.Input
          placeholder={
            noteRequired
              ? "Describe what happened (door left open, fridge defrosting, etc.)"
              : "Add any notes…"
          }
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isSubmitting}
        />
      </TextArea>

      {error && (
        <span className="text-body font-body text-error-700">{error}</span>
      )}

      <Button
        type="submit"
        variant="brand-primary"
        size="medium"
        loading={isSubmitting}
        disabled={submitDisabled}
      >
        Save reading
      </Button>
    </form>
  );
}
